import Emoji from "@core-components/emoji/Emoji";
import NeedHelpBanner from "@core-components/help-banner/NeedHelpBanner";
import Icon from "@core-components/icon/Icon";
import InfoArea from "@core-components/info-area/InfoArea";
import InfoCardGrid from "@core-components/info-card/InfoCardGrid";
import ReviewCarousel from "@core-components/reviews/ReviewCarousel";
import reviews from "@core-components/reviews/defaultReviews";
import SSHeadline from "@core-components/title/SSHeadline";
import SSLoLGradientText from "@core-components/title/components/SSLoLGradientText";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, Stack, Text } from "@mantine/core";
import RecentBlogs from "@modules/blogs/components/RecentBlogs";
import LoLFAQ from "@modules/lol/faq/LoLFAQ";
import SEO from "@modules/seo/SEO";
import type React from "react";
import { AppPopularWidget } from "../../shared/product/component/AppPopularWidget";
import { SSHandlevelWidget } from "../../shared/product/component/SSHandlevelWidget";
import SSDiscoverCarousel from "../components/DiscoverCarousel";
import HomeTitle from "../components/HomeTitle";
import MoreInfo from "../components/MoreInfo";
import { RankedWidget } from "../components/RankedWidget";
import SSReasons from "../components/Reasons";

const HomePage: React.FC = () => {
  const { t } = useAppTranslation("pages.home");
  const { t: tCommon } = useAppTranslation("common");

  return (
    <>
      <SEO description={t("seo.description")} title={t("seo.title")} />

      <HomeTitle />

      <Stack align="center" className="w-full gap-y-16 bg-primary-background">
        <AppPopularWidget />

        <NeedHelpBanner />

        <SSHandlevelWidget />

        <RankedWidget />

        <SSDiscoverCarousel />

        <InfoCardGrid
          cards={[
            {
              withBackground: false,
              icon: <Icon.Like size={60} />,
              title: tCommon("infoCards.instantDelivery.title"),
              text: tCommon("infoCards.instantDelivery.text"),
            },
            {
              withBackground: false,
              icon: <Icon.Play size={60} />,
              title: tCommon("infoCards.rankedReady.title"),
              text: tCommon("infoCards.rankedReady.text"),
            },
            {
              withBackground: false,
              icon: <Icon.AlignLeft size={60} />,
              title: tCommon("infoCards.premiumSupport.title"),
              text: tCommon("infoCards.premiumSupport.text"),
            },
          ]}
        />
        <ReviewCarousel reviews={reviews} />

        <LoLFAQ />

        <MoreInfo />

        <SSReasons />

        <Flex className="w-full" justify={"center"}>
          <InfoArea
            hasShadow
            className="!pt-10 xs:px-10 xs:py-5 pb-0"
            description={
              <Stack gap={10}>
                <SSHeadline
                  props={{
                    title: { order: 3, fz: 18 },
                    stack: { align: "left" },
                  }}
                >
                  <SSWhiteText>{t("infoArea.whyUs.title")}</SSWhiteText>
                </SSHeadline>
                <p className="mb-5">{t("infoArea.whyUs.text")}</p>

                <SSHeadline
                  props={{
                    title: { order: 3, fz: 18 },
                    stack: { align: "left" },
                  }}
                >
                  <SSWhiteText>{t("infoArea.whyLvl30.title")}</SSWhiteText>
                </SSHeadline>
                <p className="mb-5">{t("infoArea.whyLvl30.text")}</p>
                <SSHeadline
                  props={{
                    title: { order: 3, fz: 18 },
                    stack: { align: "left" },
                  }}
                >
                  <SSWhiteText>{t("infoArea.smurfNewGame.title")}</SSWhiteText>
                </SSHeadline>
                <p className="mb-5">{t("infoArea.smurfNewGame.text")}</p>
              </Stack>
            }
            fullWidth={false}
            headLine={
              <SSHeadline props={{ title: { ta: "center" } }}>
                <Emoji.Pin />
                <SSWhiteText>&nbsp;{t("infoArea.title")}</SSWhiteText>
              </SSHeadline>
            }
          />
        </Flex>

        <Stack>
          <SSHeadline
            subTitle={
              <Text className="w-2/3 pt-2 pb-6 text-center text-primary-dark-text text-sm">
                {t("articles.description")}
              </Text>
            }
            props={{ title: { ta: "center", fz: 24 } }}
          >
            <SSLoLGradientText>{t("articles.titlePrefix")}</SSLoLGradientText>
            <SSWhiteText>{t("articles.titleSuffix")}</SSWhiteText>
          </SSHeadline>

          <RecentBlogs />
        </Stack>
      </Stack>
    </>
  );
};

export default HomePage;
